import React, {useEffect, useRef, useState} from "react";
import {useTranslation} from "react-i18next";
import {equipmentListToLabel, errorMsg} from "../../../../../Apis/functions";
import HandleChange from "../../../../../Helpers/HandleChange";
import {CHECK_IN_MODEL} from "../Model/Model";
import {toast} from "react-toastify";
import {useDispatch, useSelector} from "react-redux";
import {
    EquipmentCheckInOut,
    GetAccessLogEntryById
} from "../../../../../reduxToolkit/EquipmentAndMaterial/EquipmentApi";
import {ACCESS_TYPE_MATERIAL_MODEL} from "../../../Contractors/Model/Models";

/**
 * Author: Arnulfo Ferro Zapata
 * Job Title: Fullstack
 * Email: azpata@ibl.mx
 * Created At: 03 de diciembre de 2025
 *
 * Check-out modal logic controller hook.
 *
 * This hook handles the state and logic required for the check-out modal,
 * including equipment list selections, form validation, checkbox tracking,
 * and dynamic model transformations.
 *
 * NOTE: All API calls inside this module are commented out and replaced with
 * dummy front-end placeholder logic.
 *
 * @param {Object} props
 * @param {string|number} props.itemSelected - Optional ID used to fetch data from API (dummy in this version).
 * @param {Function} [props.onSuccess] - Optional callback executed once the check-out is stored, before closing.
 * @param {boolean} [props.selectableAccessDate] - If true the guard picks the date of the exit in the modal,
 *                                                 otherwise it is stamped when the check-out is confirmed.
 * @returns {Object} Exposes modal state and handlers for UI components.
 */
export const CheckOutModalApp = ({itemSelected, onHide, onSuccess, selectableAccessDate = false}) => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const { user } = useSelector(state => state.authenticatioauthennSlice);
    const dataUser = user?.data;

    const [item, setItem] = useState({
        ...CHECK_IN_MODEL.set({
            userId: itemSelected?.userId,
            guardId: dataUser?.id,
            guardFullName: dataUser?.fullName,
            accessType: ACCESS_TYPE_MATERIAL_MODEL.EXIT.name
        }),
        // The guard picks the date of the exit when the field is shown, so it opens empty instead of with
        // the moment the modal was opened. It has to be null and not undefined for the picker to mark it as
        // missing. The flows without the field keep the number the model already carried.
        ...(selectableAccessDate ? { accessDate: null } : {})
    }); //CHECK_OUT_MODEL

    const [newSelected, setNewSelected] = useState(null);
    const [selectedList, setSelectedList] = useState([]); //EQUIPMENT_MODEL
    const [showBadgeMessage, setBadgeMessage] = useState(true);
    const { handleChange } = HandleChange({ model: item, setModel: setItem });
    const [checkedIds, setCheckedIds] = useState([]);
    const [emptyFields, setEmptyFields] = useState(false);

    // The list as it stands when an answer lands, which is not the one the closure of that request captured:
    // the guard can add materials while it travels //
    const selectedListRef = useRef(selectedList);
    selectedListRef.current = selectedList;

    // The instant the list on screen was asked for, and the one every material added on top belongs to //
    const exitDateAsked = useRef(null);

    // The date of the exit as the guard left it, not as they are writing it: the picker moves on every view
    // and on every keystroke of the field, and each move asked the materials of the entry again. It is only
    // taken when the selection is confirmed or the picker is closed, so one search is made per date chosen.
    const [committedAccessDate, setCommittedAccessDate] = useState(null);

    // The date the field holds right now, which is not the one of the model when the picker closes: confirming
    // a selection fires the change, the accept and the close in the same turn, and the model is still carrying
    // the previous date while all three run //
    const accessDateLive = useRef(item.accessDate);

    // Only the answer of the last instant asked is applied //
    const lastRequest = useRef(0);

    /**
     * Closes the informational badge.
     *
     * @function handleCloseBadge
     */
    const handleCloseBadge = () => {
        setBadgeMessage(false);
    }

    // The materials of the entry are asked for the date of the exit, so what is listed is what that exit can
    // close: one that another employee already took out is gone from the premises, and it hangs from the log
    // that employee left with, not from this one, so only the date takes it out of the list.
    // Moving the date asks again, and the materials the guard had added on top of the old instant go with it,
    // warning them: they came out of a search built for that instant, and what was inside then does not have
    // to be inside now.
    useEffect(() => {
        if (!itemSelected?.equipmentAccessLogId) return;

        // While the guard has not said when the exit happened, the materials of the entry are the ones of this
        // moment: what the employee still has inside, which is what an exit registered right now would close.
        // The modal opens with them listed, and the date the guard picks asks for the ones of that instant.
        const exitDate = resolveExitDate() ?? Date.now();
        const request = ++lastRequest.current;
        exitDateAsked.current = exitDate;

        dispatch(GetAccessLogEntryById({ id: itemSelected.equipmentAccessLogId, exitDate }))
            .then(({ payload: { data:{data, success, code} } }) => {
                // An answer of an instant the guard already left behind lists the materials of a date that is
                // no longer the one of the exit, and drops the ones added for the new one //
                if (request !== lastRequest.current) return;

                if (!success) {
                    errorMsg(code);
                    return;
                }

                applyMaterials(
                    data?.equipmentList?.length > 0
                        ? equipmentListToLabel(data.equipmentList.map(e => ({...e, saved:true})), 'id', true)
                        : [],
                    exitDate
                );
            });
    }, [itemSelected?.equipmentAccessLogId, committedAccessDate]);

    /**
     * Writes on the model the date the guard is picking, without asking for the materials of that date yet.
     *
     * @param {Date|null} value - the date the field holds
     */
    const handleAccessDateChange = value => {
        accessDateLive.current = value;
        handleChange({ target: { value, name: 'accessDate' } });
    };

    /**
     * Takes the date of the exit the guard settled on, which is what the materials of the entry are asked for.
     *
     * Called when the selection is confirmed, when the picker is closed and when the field loses the focus, so
     * the one left half picked and the date typed by hand are taken too, which never opens the picker. The
     * three run for the same selection, and only the first one changes anything: the instant is the same.
     *
     * @param {Date|null} [value=accessDateLive.current] - the date the field holds
     */
    const commitAccessDate = (value = accessDateLive.current) => {
        // A half written date is no date, so it lists nothing instead of asking for an instant that is not one //
        const time = value ? new Date(value).getTime() : null;
        setCommittedAccessDate(Number.isNaN(time) ? null : time);
    };

    /**
     * Puts on screen the materials of the entry listed for one instant, keeping the ones the guard added on
     * top of that same instant.
     *
     * Every added material carries the instant its search was built for. The ones of another instant are
     * dropped and the guard is told, because what was inside then does not have to be inside now. The ones of
     * this instant stay: the answer can land after the guard added them, and wiping those registered the exit
     * with the employee alone and the material still inside.
     *
     * The ticks survive too, trimmed to the materials that are still listed.
     *
     * @param {Array<Object>} materials - the materials of the entry listed for that instant
     * @param {number|null} exitDate - the instant they were asked for
     */
    const applyMaterials = (materials, exitDate) => {
        const added = selectedListRef.current.filter(e => !e?.saved);
        const kept = added.filter(e => e?.exitDate === exitDate);
        const list = [...materials, ...kept];

        if (kept.length < added.length)
            toast.warning(t('added_materials_removed_by_exit_date_change'));

        setSelectedList(list);
        setCheckedIds(prev => prev.filter(id => list.some(e => e.id === id)));
    };

    /**
     * Adds or removes the ID of a checked equipment item.
     *
     * @param {string} id - Equipment identifier.
     */
    const handleCheckboxChange = id => {
        setCheckedIds(prev =>
            prev.includes(id)
                ? prev.filter(itemId => itemId !== id)
                : [...prev, id]
        );
    }

    /**
     * The instant the materials of the entry are listed for.
     *
     * The date the guard settled on when the field is shown, otherwise this moment: the one the model carries
     * is from the moment the modal was opened, and an exit older than the entry it closes leaves the
     * employee listed as inside.
     *
     * The search that adds materials reads it, and the one of the entry falls back to this moment with it
     * empty, which is the list the modal opens with.
     *
     * @returns {number|null} the exit date in milliseconds, null while the guard has not settled on one
     */
    const resolveExitDate = () => selectableAccessDate
        ? committedAccessDate
        : Date.now();

    /**
     * Handles form submission and validates fields.
     * Uses dummy behavior for non-implemented API.
     *
     * @function handleCreate
     */
    const handleCheckOut = () => {
        // What the field shows is what is stamped, not the instant the lists were asked for: the guard can
        // confirm with a date written by hand that never went through the picker //
        const accessDate = selectableAccessDate
            ? (item.accessDate ? new Date(item.accessDate).getTime() : null)
            : Date.now();

        if (!accessDate) {
            setEmptyFields(true);
            return;
        }

        const body = {
            ...item.toRegister(checkedIds.map(e => ({id:e}))),
            accessDate,
            // The row this modal was opened from is the entry, so the exit closes that one and not whichever
            // entry the date lands after: the employee can have older entries of other days still open.
            entryId: itemSelected?.equipmentAccessLogId,
        };

        dispatch(EquipmentCheckInOut(body)).then(({payload:{data:{success, code}}})=>{
            if (success){
                toast.success(t('has_been_created_successfully'));
                onSuccess?.();
                onHide();
            }else
                errorMsg(code);
        });
    };

    const handleSelected = element => {
        // The instant the list it was added on top of was asked for, so a later change of the exit date takes
        // it out and one that lands late does not //
        setSelectedList(prev => [...prev, {...element, saved:false, exitDate: exitDateAsked.current}]);
        setNewSelected(null);
        setCheckedIds(prev => [...prev, element.id]);
    };

    const deleteSelected = (id) => {
        setSelectedList(prev => prev.filter(e => e.id !== id));
        // The tick goes with it: an added material is checked the moment it is added, and leaving the id
        // behind took out of the premises a material the guard had already removed from the list //
        setCheckedIds(prev => prev.filter(e => e !== id));
    };

    const removeSelectedIds = (list) => {
        return  list.filter(e => !selectedList.map(s => s.id)?.includes(e.id))
    };

    return {
        showBadgeMessage,
        handleCloseBadge,
        t,
        selectedList,
        item,
        emptyFields,
        handleChange,
        handleCheckboxChange,
        checkedIds,
        newSelected,
        setNewSelected,
        handleSelected,
        handleCheckOut,
        deleteSelected,
        removeSelectedIds,
        resolveExitDate,
        handleAccessDateChange,
        commitAccessDate
    };

};
